import React, { useState, useEffect } from 'react';
import { Link, useLocation  } from 'react-router-dom';
import { useCompanyInfo } from '../../context/CompanyInfoContext';
import {
  HomeIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UsersIcon,
  BriefcaseIcon,
  CalculatorIcon,
  FolderIcon,
  TagIcon,
  WrenchScrewdriverIcon,
  ShoppingCartIcon,
  ListBulletIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  TruckIcon,
  ChartBarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { CalendarIcon } from 'lucide-react';  
import { useRoutePath } from '../../utils/getRoutePath';

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const { companyInfo } = useCompanyInfo()
  const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
  const capitalize = (str) => str?.charAt(0)?.toUpperCase() + str?.slice(1) || '';
  const getRoutePath = useRoutePath();
   
  useEffect(() => {
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
  
    window.addEventListener('resize', handleResize);
  
    return () => {
     
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile, isOpen]);

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
       [menuName]: !prev[menuName]
     }));
   
  };
   
  let userMenuItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: 'Calendar',
      href: '/calendar',
      icon: CalendarIcon,
    },
    {
      name: 'Customers',
      href: '/customers',
      icon: UsersIcon,
      submenu: [
        { name: 'Customer List', href: '/customers' },
        { name: 'Add Customer', href: '/customers/new' },
         
      ],
    },
    {
      name: 'Tasks',
      href: '/tasks',
      icon: ClipboardDocumentListIcon,
      submenu: [
        { name: 'Task List', href: '/tasks' },
        { name: 'Add Task', href: '/tasks/new' },
         
      ],
    },
    {
      name: 'Jobs',
      href: '/jobs',
      icon: BriefcaseIcon,
      submenu: [
        { name: 'Job List', href: '/jobs' },
        { name: 'Add Job', href: '/jobs/new' },
      ],
    },
    {
      name: 'Projects',
      href: '/projects',
      icon: FolderIcon,
      submenu: [
        { name: 'Project List', href: '/projects' },
        { name: 'Add Project', href: '/projects/create' },
      ],
    },
    {
      name: 'Estimations',
      href: '/estimations',
      icon: CalculatorIcon,
      submenu: [
        { name: 'Estimation List', href: '/estimations' },
        { name: 'Add Estimation', href: '/estimations/new' },
      ],
    },
    {
      name: 'Service Categories',
      href: '/service-categories',
      icon: TagIcon,
      submenu: [
        { name: 'Service Categories List', href: '/service-categories' },
        { name: 'Add Service Categories', href: '/service-categories/create' },
      ],
    },
    {
      name: 'Service',
      href: '/services',
      icon: WrenchScrewdriverIcon,
      submenu: [
        { name: 'Service List', href: '/services' },
        { name: 'Add Service', href: '/services/new' },
      ],
    },
    {
      name: 'Product Categories',
      href: '/product-categories',
      icon: TagIcon,
      submenu: [
        { name: 'Product Categories List', href: '/product-categories' },
        { name: 'Add Product Categories', href: '/product-categories/create' },
      ],
    },
    {
      name: 'Products',
      href: '/products',
      icon: ShoppingCartIcon,
      submenu: [
        { name: 'Product List', href: '/products' },
        { name: 'Add Product', href: '/products/create' },
      ],
    },
    {
      name: 'Vendors',
      href: '/vendors',
      icon: TruckIcon,
      submenu: [
        { name: 'Vendor List', href: '/vendors' },
        { name: 'Add Vendor', href: '/vendors/create' },
      ],
    },
    {
      name:'Companies',
      href:'/company-info',
      icon: BuildingOfficeIcon,
      submenu:[
        {name:'Company List',href:'/company-info'},
        {name:'Add Company',href:'/company-info/create'},
      ]
    },
    {
      name:'Company Workforces',
      href:'/company-workforces',
      icon: UsersIcon,
      submenu:[
        {name:'Company Workforce List',href:'/company-workforces'},
        {name:'Add Company Workforce',href:'/company-workforces/create'},
      ]
    },
    {
      name: 'List Management',
      href: '/list-management',
      icon: ListBulletIcon,
      submenu: [
        { name: 'Expense Categories', href: '/expense-categories' },
        { name: 'Job Categories', href: '/job-categories' },
        { name: 'Time Off Request Types', href: '/time-off-request-types' },
        { name: 'Payment Methods', href: '/payment-methods' },
        { name: 'Payment Terms', href: '/payment-terms' },
        { name: 'Equipment Manufacturers', href: '/equipment-manufacturers' },
        { name: 'Equipment Types', href: '/equipment-types' },
      ],
    },
    {
      name: 'Tag Management',
      href: '/tag-management',
      icon: TagIcon,
      submenu: [
        { name: 'Customer Tags', href: '/customer-tags' },
        { name: 'Estimate & Job Tags', href: '/estimate-job-tags' },
         
      ],
    }, 
    {
      name: 'Settings',
      href: '/settings',
      icon: Cog6ToothIcon,
      submenu: [
       
        { name: 'Communication  Templates', href: '/communication-templates' },
      ],
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: ChartBarIcon,
      submenu: [
       
        { name: 'Report Dashboard', href: '/reports' },
      ],
    },
  ]; 

  if (user.role_id !== 1 && companyInfo?.data?.length >= 1) {
    userMenuItems = userMenuItems.map(item => {
          if (item.href === '/company-info' && item.submenu) {
              item.submenu = item.submenu.filter(sub => sub.href !== '/company-info/create');
          }
          return item;
      });
  }
  
  const adminMenuItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
    },
    {
      name:'Roles',
      href:'/roles',
      icon: UserGroupIcon,
      submenu:[
        {name:'Role List',href:'/roles'},
        {name:'Add Role',href:'/roles/create'},
      ]
    },
    {
      name:'Companies',
      href:'/company-info',
      icon: BuildingOfficeIcon,
      submenu:[
        {name:'Company List',href:'/company-info'},
        {name:'Add Company',href:'/company-info/create'},
      ]
    },
    {
      name:'Company Workforces',
      href:'/company-workforces',
      icon: ListBulletIcon,
      submenu:[
        {name:'Company Workforce List',href:'/company-workforces'},
        {name:'Add Company Workforce',href:'/company-workforces/create'},
      ]
    },
     
    
  ];

  const menuItems = (user && typeof user === 'object' && (user.role_id === 1 || user.role_id === '1')) ? adminMenuItems : userMenuItems;

  function findMenuNameByFirstSegment(menuItems, firstSegment) {
    for (const item of menuItems) {
      if (item.submenu) {
        for (const subItem of item.submenu) {
          // Check if subItem.href includes or matches firstSegment
          if (subItem.href.includes(firstSegment)) {
            return item.name;  // Return the parent menu name
          }
        }
      }
      // Also check if top-level href matches firstSegment if needed:
      if (item.href && item.href.includes(firstSegment)) {
        return item.name;
      }
    }
    return null;  // Not found
  }

  useEffect(() => {
    const onLoad = () => {
      const timer = setTimeout(() => {
         const menuName = findMenuNameByFirstSegment(menuItems, firstSegment)
        toggleMenu(capitalize(menuName));
      }, 0); // 1 second delay
  
      // Cleanup timeout if the component unmounts before timeout fires
      return () => clearTimeout(timer);
    };
  
    window.addEventListener('load', onLoad);
  
    // Cleanup event listener and timeout on unmount
    return () => {
      window.removeEventListener('load', onLoad);
    };
  }, [firstSegment,menuItems]);

  
    
  return (
    <>
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white transform transition-transform duration-200 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:inset-auto lg:z-30`}
      >
        <div className="flex flex-col h-full">
          {/* User Profile */}
          <div className="flex items-center px-6 py-4 border-b border-gray-200">
            <div className="flex-shrink-0">
              <img
                src={user?.image?.path || '/assets/images/default-avatar.png'}
                alt={user?.name}
                className="h-12 w-12 rounded-full bg-gray-100 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/images/default-avatar.png';
                }}
              />
            </div>
            <div className="ml-3">
              <p className="text-base font-semibold text-gray-900">{user?.first_name + ' ' + user?.last_name || 'User'}</p>
              <p className="text-sm text-gray-600 capitalize">{user?.role?.name || 'user'}</p>
            </div>
          </div>

          {/* Sidebar navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <div key={item.name} >
                {item.submenu ? (
                  <div>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={`flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        location.pathname.startsWith(getRoutePath(item.href)) ||
                        (item.submenu && item.submenu.some(subItem => location.pathname.startsWith(getRoutePath(subItem.href))))
                          ? 'bg-[#009efb] text-white shadow-md'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon
                        className={`mr-3 h-5 w-5 ${
                        //   location.pathname.startsWith(getRoutePath(item.href))
                        //     ? 'text-white'
                        //     : 'text-gray-500 group-hover:text-gray-900'
                        // }
                        (item.submenu && item.submenu.some(subItem => location.pathname.startsWith(getRoutePath(subItem.href))))
                          ? 'text-white'
                          : 'text-whitetext-gray-500 group-hover:text-gray-900'
                      }`}
                        aria-hidden="true"
                      />
                      {item.name}
                      {expandedMenus[item.name] ? (
                        <ChevronUpIcon className={`ml-auto h-4 w-4 ${
                          (item.submenu && item.submenu.some(subItem => location.pathname.startsWith(getRoutePath(subItem.href))))
                            ? 'text-white'
                            : 'text-gray-500'
                          }`} />
                      ) : (
                        <ChevronDownIcon className={`ml-auto h-4 w-4 ${
                          (item.submenu && item.submenu.some(subItem => location.pathname.startsWith(getRoutePath(subItem.href))))
                            ? 'text-white'
                            : 'text-gray-500'
                          }`} />
                      )}
                    </button>
                    {expandedMenus[item.name] && (
                      <div className="mt-2 ml-4 space-y-1.5">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={getRoutePath(String(subItem.href || ''))}
                            className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-200 ${
                              location.pathname === getRoutePath(String(subItem.href || ''))
                                ? 'text-[#009efb] '
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => isMobile && onClose()}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={getRoutePath(String(item.href || ''))}
                    className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      location.pathname === getRoutePath(String(item.href || ''))
                        ? 'bg-[#009efb] text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => isMobile && onClose()}
                  >
                    <item.icon
                      className={`mr-3 h-5 w-5 ${
                        location.pathname === getRoutePath(String(item.href || ''))
                          ? 'text-white'
                          : 'text-gray-500 group-hover:text-gray-900'
                      }`}
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && isMobile && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
};

export default Sidebar; 